// Componente ProductList para e-commerce
// Muestra una lista de productos en grid optimizado para mobile
// Enfocado en experiencia de donación

import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import { obtenerProductos } from '../../services/productosService';
import { crearOrden } from '../../services/ordenesService';
import { useAuth } from '../../hooks/useAuth';

/**
 * Componente de lista de productos
 * Muestra productos en grid con funcionalidad de donación
 */
const ProductList = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const { user } = useAuth();

  // Cargar productos al montar
  useEffect(() => {
    cargarProductos();
  }, []);

  /**
   * Carga los productos desde Supabase
   */
  const cargarProductos = async () => {
    try {
      setLoading(true);
      const data = await obtenerProductos();
      setProductos(data);
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Inicia el proceso de compra de un producto
   * @param {Object} producto - Producto a comprar
   * @param {number} cantidad - Cantidad de unidades a comprar
   */
  const handleBuy = async (producto, cantidad = 1) => {
    if (!user) {
      alert('Debes iniciar sesión para comprar');
      return;
    }

    try {
      setProcessingPayment(producto.id);

      // Llamar a la Edge Function create-preference
      const edgeFunctionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-preference`;
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          producto_id: producto.id,
          user_id: user.id,
          cantidad: cantidad
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error completo:', errorData);
        throw new Error(errorData.error || errorData.details || 'Error creando preferencia de pago');
      }

      // Redirigir a Mercado Pago
      if (data.init_point) {
        window.location.href = data.init_point;
      } else {
        throw new Error('No se recibió URL de pago');
      }

    } catch (error) {
      console.error('Error en compra:', error);
      alert(`Error al procesar la compra: ${error.message}`);
    } finally {
      setProcessingPayment(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (productos.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No hay productos disponibles</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Grid compacto con ancho limitado en desktop */}
      <div className="
        items-start 
        justify-center 
        grid 
        gap-6 
        grid-cols-1 
        lg:grid-cols-2
        lg:mx-auto
      ">
        {productos.map((producto) => (
          <ProductCard
            key={producto.id}
            producto={producto}
            onBuy={handleBuy}
            loading={processingPayment === producto.id}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductList;
